import React, { useState, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import { normNaamFase1 } from '../utils/normNaam.js';

const PLUGIN_URL = typeof window !== 'undefined' && window.EUREKA_PLUGIN_URL
  ? window.EUREKA_PLUGIN_URL : '/';
const EILANDERS_URL = `${PLUGIN_URL}uitslagen-output/eilanders.json`;

const S = {
  card: {
    background: '#fff',
    border: '1px solid #e0e8ff',
    borderRadius: 8,
    padding: '1em 1.2em',
    marginBottom: '0.5em',
  },
  sectionTitle: {
    background: '#001a4d',
    color: '#fff',
    padding: '0.5em 1em',
    borderRadius: 6,
    fontSize: '1em',
    fontWeight: 'bold',
    marginBottom: '0.8em',
    marginTop: '1.5em',
  },
  th: {
    padding: '0.4em 0.7em',
    borderBottom: '2px solid #b4c002',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    textAlign: 'left',
    background: '#f0f4ff',
  },
  td: {
    padding: '0.35em 0.7em',
    borderBottom: '1px solid #eee',
    verticalAlign: 'top',
  },
  badge: {
    display: 'inline-block',
    background: '#b4c002',
    color: '#222',
    borderRadius: 10,
    padding: '0.1em 0.5em',
    fontSize: '0.8em',
    fontWeight: 'bold',
    marginLeft: '0.4em',
  },
  statBox: {
    background: '#f0f4ff',
    border: '1px solid #c8d4ff',
    borderRadius: 8,
    padding: '1em',
    textAlign: 'center',
    flex: '1 1 140px',
  },
  statNum: {
    fontSize: '2em',
    fontWeight: 'bold',
    color: '#001a4d',
    display: 'block',
  },
  statLabel: {
    fontSize: '0.85em',
    color: '#555',
    marginTop: '0.2em',
  },
};

function StatBoxes({ data }) {
  return (
    <div style={{ marginBottom: '1.5em' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.3em' }}>
        <span style={{ fontSize: '0.75em', color: '#aaa' }}>v{import.meta.env.VITE_APP_VERSION}</span>
      </div>
      <div style={{ display: 'flex', gap: '1em', flexWrap: 'wrap' }}>
        <div style={S.statBox}>
          <span style={S.statNum}>{data.aantalEilanders?.toLocaleString('nl-NL')}</span>
          <div style={S.statLabel}>unieke eilanders</div>
        </div>
        <div style={S.statBox}>
          <span style={S.statNum}>{data.aantalDeelnames?.toLocaleString('nl-NL')}</span>
          <div style={S.statLabel}>deelnames totaal</div>
        </div>
        <div style={S.statBox}>
          <span style={S.statNum}>{data.aantalEdities}</span>
          <div style={S.statLabel}>edities (2012–heden)</div>
        </div>
      </div>
    </div>
  );
}

const TOON_STANDAARD = 10;

function TabelMetUitvouw({ headers, rows, renderRij }) {
  const zichtbaar = rows.slice(0, TOON_STANDAARD);
  const rest = rows.slice(TOON_STANDAARD);
  const tabelStijl = { width: '100%', borderCollapse: 'collapse', fontSize: '0.93em' };

  return (
    <>
      <table style={tabelStijl}>
        <thead>
          <tr>{headers.map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {zichtbaar.map((item, i) => renderRij(item, i))}
        </tbody>
      </table>
      {rest.length > 0 && (
        <details style={{ marginTop: '0.2em' }}>
          <summary style={{
            cursor: 'pointer', fontSize: '0.88em', color: '#001a4d',
            padding: '0.4em 0.2em', userSelect: 'none',
          }}>
            ▶ Toon {rest.length} meer (#{TOON_STANDAARD + 1}–#{rows.length})
          </summary>
          <table style={{ ...tabelStijl, marginTop: '0.2em' }}>
            <tbody>
              {rest.map((item, i) => renderRij(item, TOON_STANDAARD + i))}
            </tbody>
          </table>
        </details>
      )}
    </>
  );
}

function TrouwsteDeelnemers({ lijst }) {
  return (
    <>
      <div style={S.sectionTitle}>🏅 Trouwste eilanders</div>
      <TabelMetUitvouw
        headers={['#', 'Naam', 'Edities', 'Eerste editie', 'PR (enkel)']}
        rows={lijst}
        renderRij={(p, i) => (
          <tr key={p.naam} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff' }}>
            <td style={{ ...S.td, color: '#999', width: '2em' }}>{i + 1}</td>
            <td style={{ ...S.td, fontWeight: 'bold' }}>{p.naam}</td>
            <td style={S.td}>{p.aantalEdities}<span style={S.badge}>{p.aantalEdities}×</span></td>
            <td style={S.td}>{p.eersteEditie}</td>
            <td style={S.td}>{p.pr?.tijd || '—'} {p.pr?.jaar ? <span style={{ color: '#999', fontSize: '0.85em' }}>({p.pr.jaar})</span> : null}</td>
          </tr>
        )}
      />
    </>
  );
}

function SnelsteTijdenTabel({ lijst, badge }) {
  return (
    <TabelMetUitvouw
      headers={['#', 'Naam', 'Tijd', 'Jaar']}
      rows={lijst}
      renderRij={(p, i) => (
        <tr key={`${p.naam}-${p.jaar}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff' }}>
          <td style={{ ...S.td, color: '#999', width: '2em' }}>{i + 1}</td>
          <td style={{ ...S.td, fontWeight: 'bold' }}>{p.naam}</td>
          <td style={{ ...S.td, fontFamily: 'monospace', fontWeight: 'bold', color: '#001a4d' }}>{p.tijd}</td>
          <td style={S.td}>{p.jaar}</td>
        </tr>
      )}
    />
  );
}

function SnelsteTijden({ stats }) {
  const [actief, setActief] = useState('heren-enkel');

  const tabs = [
    { id: 'heren-enkel', label: '♂ Enkel', lijst: stats.snelsteTijdenHeren || [] },
    { id: 'dames-enkel', label: '♀ Enkel', lijst: stats.snelsteTijdenDames || [] },
    { id: 'heren-dubbel', label: '♂ Dubbel', lijst: stats.snelsteTijdenDubbelHeren || [] },
    { id: 'dames-dubbel', label: '♀ Dubbel', lijst: stats.snelsteTijdenDubbelDames || [] },
  ];

  const huidig = tabs.find((t) => t.id === actief) || tabs[0];

  return (
    <>
      <div style={S.sectionTitle}>⚡ Snelste tijden ooit</div>
      <div style={{ display: 'flex', gap: '0.4em', marginBottom: '0.8em', flexWrap: 'wrap' }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActief(tab.id)}
            style={{
              padding: '0.3em 0.8em',
              fontSize: '0.88em',
              fontWeight: actief === tab.id ? 'bold' : 'normal',
              background: actief === tab.id ? '#001a4d' : '#f0f4ff',
              color: actief === tab.id ? '#fff' : '#001a4d',
              border: '1px solid #001a4d',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            {tab.label}
            <span style={{ ...S.badge, marginLeft: '0.4em' }}>{tab.lijst.length}</span>
          </button>
        ))}
      </div>
      <SnelsteTijdenTabel lijst={huidig.lijst} />
    </>
  );
}

function MeestGewonnen({ lijst }) {
  return (
    <>
      <div style={S.sectionTitle}>🏆 Meeste overwinningen</div>
      <TabelMetUitvouw
        headers={['#', 'Naam', 'Winsten', 'Jaren']}
        rows={lijst}
        renderRij={(p, i) => (
          <tr key={p.naam} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff' }}>
            <td style={{ ...S.td, color: '#999', width: '2em' }}>{i + 1}</td>
            <td style={{ ...S.td, fontWeight: 'bold' }}>{p.naam}</td>
            <td style={S.td}><span style={S.badge}>{p.aantalWinsten}×</span></td>
            <td style={{ ...S.td, fontSize: '0.88em', color: '#555' }}>
              {p.winsten?.map((w) => `${w.jaar} (${w.tijd})`).join(', ')}
            </td>
          </tr>
        )}
      />
    </>
  );
}

function TopSteden({ lijst }) {
  const top = lijst.slice(0, 20);
  const max = top[0]?.aantalDeelnames || 1;
  return (
    <>
      <div style={S.sectionTitle}>📍 Meeste deelnames per stad</div>
      <TabelMetUitvouw
        headers={['#', 'Stad', 'Deelnames', 'Unieke deelnemers', '']}
        rows={top}
        renderRij={(s, i) => (
          <tr key={s.naam} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff' }}>
            <td style={{ ...S.td, color: '#999', width: '2em' }}>{i + 1}</td>
            <td style={{ ...S.td, fontWeight: 'bold' }}>{s.naam}</td>
            <td style={S.td}>{s.aantalDeelnames}</td>
            <td style={S.td}>{s.uniekeDeelnemers}</td>
            <td style={S.td}>
              <div style={{
                height: 8, borderRadius: 4, background: '#b4c002',
                width: `${Math.round((s.aantalDeelnames / max) * 100)}%`, minWidth: 4,
              }} />
            </td>
          </tr>
        )}
      />
    </>
  );
}

function ZoekEilander({ eilanders }) {
  const [zoek, setZoek] = useState('');

  // Bouw een genormaliseerde zoekindex: originele eilander + genormaliseerde naam
  const eilandersMetNorm = useMemo(() => eilanders.map((e) => ({
    ...e,
    _normNaam: normNaamFase1(e.naam), // diacrieten + komma-notatie genormaliseerd
  })), [eilanders]);

  // Fuse.js op de genormaliseerde naam voor fuzzy matching
  const fuse = useMemo(() => new Fuse(eilandersMetNorm, {
    keys: ['_normNaam'],
    threshold: 0.25,
    distance: 100,
    minMatchCharLength: 2,
    ignoreLocation: true,
  }), [eilandersMetNorm]);

  const gevonden = useMemo(() => {
    if (zoek.trim().length < 2) return [];
    // Normaliseer ook de zoekterm
    const normZoek = normNaamFase1(zoek);
    return fuse.search(normZoek).map((r) => r.item).slice(0, 20);
  }, [fuse, zoek]);

  return (
    <>
      <div style={S.sectionTitle}>🔍 Zoek een eilander</div>
      <div style={{ position: 'relative', marginBottom: '1em' }}>
        <input
          type="text"
          placeholder="Zoek op naam..."
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
          style={{
            width: '100%',
            padding: '0.6em 2.5em 0.6em 0.8em',
            fontSize: '1em',
            border: '1.5px solid #b4c002',
            borderRadius: 6,
            boxSizing: 'border-box',
          }}
        />
        {zoek && (
          <button
            type="button"
            onClick={() => setZoek('')}
            style={{ position: 'absolute', right: '0.6em', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#888', fontSize: '1.3em' }}
          >×</button>
        )}
      </div>

      {zoek.trim().length >= 2 && gevonden.length === 0 && (
        <p style={{ color: '#888' }}>Geen eilander gevonden voor &ldquo;{zoek}&rdquo;.</p>
      )}

      {gevonden.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.93em' }}>
          <thead>
            <tr>
              <th style={S.th}>Naam</th>
              <th style={S.th}>Edities</th>
              <th style={S.th}>Jaren</th>
              <th style={S.th}>PR (enkel)</th>
            </tr>
          </thead>
          <tbody>
            {gevonden.map((e, i) => (
              <tr key={e.naam} style={{ background: i % 2 === 0 ? '#fff' : '#f8f9ff' }}>
                <td style={{ ...S.td, fontWeight: 'bold' }}>{e.naam}</td>
                <td style={S.td}><span style={S.badge}>{e.aantalEdities}×</span></td>
                <td style={{ ...S.td, fontSize: '0.85em', color: '#555' }}>
                  {e.edities?.map((ed) => ed.jaar).join(', ')}
                </td>
                <td style={{ ...S.td, fontFamily: 'monospace' }}>{e.pr?.tijd || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export default function EilandersAllerTijden() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fout, setFout] = useState(null);

  useEffect(() => {
    fetch(EILANDERS_URL)
      .then((r) => {
        if (r.status === 404) throw new Error('404');
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => { setData(json); setLoading(false); })
      .catch((e) => {
        const msg = e.message === '404'
          ? `eilanders.json niet gevonden.\n\nUpload het bestand via WordPress → Media → Nieuw bestand toevoegen.\nVerwacht pad: ${EILANDERS_URL}`
          : `Kan eilanders.json niet laden: ${e.message}\n\nVerwacht pad: ${EILANDERS_URL}`;
        setFout(msg);
        setLoading(false);
      });
  }, []);

  if (loading) return <div style={{ padding: '2em', textAlign: 'center', color: '#666' }}>Eilanders laden...</div>;

  if (fout) return (
    <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, padding: '1em 1.2em', color: '#7a4f00', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.9em' }}>
      <strong>⚠ Eilanders aller tijden kon niet worden geladen</strong>
      {'\n\n'}{fout}
    </div>
  );

  const { stats, eilanders } = data;

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', maxWidth: 900 }}>
      <StatBoxes data={data} />
      <TrouwsteDeelnemers lijst={stats.trouwsteDeelnemers || []} />
      <SnelsteTijden stats={stats} />
      <MeestGewonnen lijst={stats.meestgewonnen || []} />
      <TopSteden lijst={stats.topSteden || []} />
      <ZoekEilander eilanders={eilanders || []} />
      <p style={{ fontSize: '0.8em', color: '#aaa', marginTop: '2em' }}>
        Gegenereerd op {data.gegenereerd} · {data.aantalEilanders?.toLocaleString('nl-NL')} unieke eilanders uit {data.aantalDeelnames?.toLocaleString('nl-NL')} deelnames
      </p>
    </div>
  );
}
